<template>
  <!--
    The Mystics tab (2026-10-26, Claude outputs/mystics-tab-2026-10-26/: DESIGN.md
    r2 + r3/r3b, prototype.html). Your Mystics strip and compact drops:
    Claude outputs/your-mystics-strip-2026-10-30/ (DESIGN.md §1–4, Motion). Top to bottom: the ⓘ line (until the first ☆
    or ×), Your Mystics (home + ☆, their order), the box, Meet the Mystics with
    one sort chip. Tap any Mystic for its preview. Nothing here ranks by
    reading counts; the server counts people (utils/mystics-tab.js).
  -->
  <div ref="rootEl" class="mt" :class="{ rm: reducedMotion, desk: isDesk }">
    <LoadState
      v-if="!data"
      :loading="loading" :failed="failed"
      message="Couldn't reach the Mystics just now."
      loading-label="Gathering the Mystics"
      @retry="load"
    />
    <!-- See all › from "Today from the Mystics": the feed in the tab's place. -->
    <div v-else-if="dropsFull" class="wrap enter">
      <TodayFromMystics full @back="dropsFull = false" @open-mystic="openDrop" />
    </div>
    <div v-else class="wrap enter">
      <!-- Your Mystics (your-mystics-strip 2026-10-30 §1–3): one Mystic = the home
           card + the dashed slot; two or more = small tiles in their order. -->
      <section data-testid="your-mystics" aria-labelledby="mt-mine-h">
        <div class="sec">
          <h3 id="mt-mine-h">Your Mystics</h3>
          <span class="right">
            <span v-if="isDesk && mine.order.length > 1 && !reorder" v-show="edge.over" class="arrows" data-testid="strip-arrows">
              <button :disabled="!edge.left" aria-label="Scroll back" @click="scrollStrip(-1)">‹</button>
              <button :disabled="!edge.right" aria-label="Scroll on" @click="scrollStrip(1)">›</button>
            </span>
            <button v-if="reorder" class="gold done" data-testid="reorder-done" @click="finishReorder">Done</button>
            <button v-else-if="mine.order.length > 1" class="hint" data-testid="reorder" @click="reorder = true">
              {{ isDesk ? 'Drag to reorder' : 'Hold to reorder' }}
            </button>
          </span>
        </div>

        <div v-if="mine.order.length < 2" class="solo">
          <div
            v-if="solo" class="hc" data-testid="mine-tile" :data-sub="solo"
            role="button" tabindex="0" :aria-label="nameOf(solo) + (solo === mine.home ? ', your home Mystic' : '')"
            @click="onTileClick(solo)" @keydown.enter.self="onTileClick(solo)"
          >
            <div class="ph pt" :data-ph="solo">
              <img v-if="art(by[solo])" :src="art(by[solo])" :alt="nameOf(solo)" draggable="false" />
              <div v-else class="pt-ph"></div>
            </div>
            <div class="bd">
              <div class="nmrow">
                <span class="nm">{{ nameOf(solo) }}</span>
                <span v-if="solo === mine.home" class="homepill"><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 3 2 11h3v9h5v-6h4v6h5v-9h3z"/></svg>Home</span>
              </div>
              <template v-if="soloDrop">
                <div class="today"><span class="ey">Today</span><span class="tt">{{ soloDrop.title }}</span></div>
                <p v-if="soloDrop.teaser" class="ln">{{ soloDrop.teaser }}</p>
              </template>
              <p v-else-if="by[solo] && by[solo].teaser" class="ln">{{ by[solo].teaser }}</p>
            </div>
            <button
              class="hstar" data-testid="unstar"
              :aria-label="solo === mine.home ? 'Your home Mystic' : `Remove ${nameOf(solo)} from Your Mystics`"
              @click.stop="unstar(solo)" @keydown.enter.stop
            >★</button>
          </div>
          <button class="gslot" data-testid="empty-slot" :aria-label="`${EMPTY_SLOT}. Go to Meet the Mystics.`" @click="toMeet">
            <span v-if="faces.length" class="faces"><img v-for="f in faces" :key="f" :src="art(by[f])" alt="" /></span>
            <span class="gt">{{ EMPTY_SLOT }}</span><span class="ar" aria-hidden="true">↓</span>
          </button>
        </div>

        <div v-else class="strip">
          <div
            ref="mineEl" class="mine2" :class="{ reorder, 'fade-l': edge.left, 'fade-r': edge.right }"
            @scroll.passive="readEdges" @contextmenu.prevent
          >
            <div
              v-for="(sub, i) in mine.order" :key="sub"
              class="st" :class="{ home: sub === mine.home, dragging: drag && drag.sub === sub && !drag.pending, 'new-in': justAdded === sub }"
              data-testid="mine-tile" :data-sub="sub"
              @pointerdown="onTileDown($event, sub)"
            >
              <div
                class="sp pt" role="button" tabindex="0"
                :aria-label="nameOf(sub) + (sub === mine.home ? ', your home Mystic' : '')"
                @click="onTileClick(sub)" @keydown.enter.self="onTileClick(sub)"
              >
                <div class="im" :data-ph="sub">
                  <img v-if="art(by[sub])" :src="art(by[sub])" alt="" draggable="false" />
                  <div v-else class="pt-ph"></div>
                </div>
                <span v-if="sub === mine.home" class="homepill"><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 3 2 11h3v9h5v-6h4v6h5v-9h3z"/></svg>Home</span>
                <button
                  class="sstar" data-testid="unstar"
                  :aria-label="sub === mine.home ? 'Your home Mystic' : `Remove ${nameOf(sub)} from Your Mystics`"
                  @click.stop="unstar(sub)" @pointerdown.stop @keydown.enter.stop
                >★</button>
              </div>
              <div class="snm">{{ nameOf(sub) }}</div>
              <div class="mv">
                <button :disabled="i === 0" aria-label="Move earlier" @click="moveTile(sub, -1)" @pointerdown.stop>‹</button>
                <button :disabled="i === mine.order.length - 1" aria-label="Move later" @click="moveTile(sub, 1)" @pointerdown.stop>›</button>
              </div>
            </div>
            <button v-if="showSlot" class="gslot" data-testid="empty-slot" :aria-label="`${EMPTY_SLOT}. Go to Meet the Mystics.`" @click="toMeet">
              <span v-if="faces.length" class="faces"><img v-for="f in faces" :key="f" :src="art(by[f])" alt="" /></span>
              <span class="gt">{{ EMPTY_SLOT }}</span>
            </button>
          </div>
        </div>
      </section>

      <!-- The box -->
      <div ref="findEl" class="find" :class="{ open: focus }" data-testid="mystic-search">
        <label class="search">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>
          <input
            v-model="q" :placeholder="PLACEHOLDER" autocomplete="off" aria-label="Find a Mystic" enterkeyhint="search"
            @focus="focus = true" @input="onType" @keydown.enter.prevent="submit" @keydown.esc="focus = false"
          />
          <button v-if="q || result" class="x" aria-label="Clear" @click.prevent="clearSearch">×</button>
        </label>
        <div v-if="focus" class="suggest" data-testid="suggest" @mousedown.prevent>
          <template v-if="!q.trim()">
            <div class="sg-h">What you’re going through</div>
            <div class="sg-chips">
              <button v-for="t in data.topics" :key="t.key" class="chip" @click="pickTopic(t.key)">{{ t.label }}</button>
            </div>
            <div class="sg-h">A vibe</div>
            <div class="sg-chips">
              <button v-for="v in data.vibes" :key="v.key" class="chip" @click="pickVibe(v.key)">{{ v.label }}</button>
            </div>
            <div class="sg-foot">Or type it in your own words, or a Mystic’s name.</div>
          </template>
          <template v-else>
            <template v-if="sugg.mystics.length">
              <div class="sg-h">Mystics</div>
              <button v-for="m in sugg.mystics" :key="m.sub_domain" class="sg-row" @click="pickName(m.sub_domain)">
                <img v-if="art(m)" :src="art(m)" alt="" /><span class="k">{{ m.name }}</span><span class="s">{{ m.authored_by ? `by ${m.authored_by}` : '' }}</span>
              </button>
            </template>
            <template v-if="sugg.topics.length">
              <div class="sg-h">What you’re going through</div>
              <button v-for="t in sugg.topics" :key="t.key" class="sg-row" @click="pickTopic(t.key, q)">
                <span class="gl">✧</span><span class="k">{{ t.label }}</span><span class="s">who reads it well</span>
              </button>
            </template>
            <template v-if="sugg.vibes.length">
              <div class="sg-h">A vibe</div>
              <button v-for="v in sugg.vibes" :key="v.key" class="sg-row" @click="pickVibe(v.key)">
                <span class="gl">~</span><span class="k">{{ v.label }}</span><span class="s">{{ v.count }} Mystic{{ v.count === 1 ? '' : 's' }}</span>
              </button>
            </template>
            <template v-if="sugg.practice">
              <div class="sg-h">A practice</div>
              <div class="sg-foot first">{{ sugg.practice }}</div>
            </template>
            <div class="sg-foot">{{ sugg.foot }}</div>
          </template>
        </div>
      </div>

      <!-- Results -->
      <div v-if="result" class="res" data-testid="results">
        <div class="res-h"><h3>{{ result.title }}</h3><button @click="clearSearch">Clear</button></div>
        <div v-if="!result.picks.length" class="empty">{{ result.line || NO_MATCH }}</div>
        <template v-else>
          <div class="pick-list">
            <div
              v-for="(sub, i) in result.picks" :key="sub" class="pick" data-testid="pick" :data-sub="sub"
              :style="{ animationDelay: `${0.06 + i * 0.1}s` }" @click="openPage(sub)"
            >
              <div class="hd">
                <img v-if="art(by[sub])" :src="art(by[sub])" alt="" />
                <div>
                  <div class="nm">{{ nameOf(sub) }}<span v-if="mine.order.includes(sub)" class="tag">{{ sub === mine.home ? 'Home' : 'Yours' }}</span></div>
                  <div v-if="by[sub] && by[sub].authored_by" class="by">by {{ by[sub].authored_by }}</div>
                </div>
              </div>
              <div v-if="lineFor(sub, result.key)">
                <div class="qlabel">How {{ shortName(by[sub]) }} might open</div>
                <p class="quote">“{{ lineFor(sub, result.key) }}”</p>
              </div>
              <p v-else class="quote soft">{{ teaserOf(by[sub]) }}</p>
              <div class="ft">
                <span class="meta"><template v-for="(seg, j) in facts(by[sub])" :key="seg"><span v-if="j" class="s">·</span>{{ seg }}</template></span>
                <button class="gold" data-testid="pick-read" @click.stop="getReading(sub)">Get a reading</button>
              </div>
            </div>
          </div>
          <div class="after">
            <template v-if="result.free">Your words go with you into the reading. </template>By fit, not popularity.
            <button v-if="result.kind === 'topic'" @click="emit('show-lessons', result.key)">Lessons on {{ topicLabel(result.key).toLowerCase() }}</button>
          </div>
        </template>
      </div>

      <!-- Today from the Mystics (mystic-page 2026-10-29, PNGs 07/08) -->
      <TodayFromMystics :hide-sub="hideSub" :mine="mine.order" @see-all="dropsFull = true" @open-mystic="openDrop" @note="toast" />

      <!-- Meet the Mystics -->
      <section data-testid="everyone-here" aria-labelledby="mt-all-h">
        <div class="sec">
          <h3 id="mt-all-h">Meet the Mystics</h3>
          <div class="sortwrap">
            <button class="sortchip" data-testid="sort-chip" :aria-expanded="sortOpen" aria-haspopup="menu" @click="sortOpen = !sortOpen">
              {{ sortLabel(sort) }} <span aria-hidden="true">▾</span>
            </button>
            <div v-if="sortOpen" class="sortmenu" role="menu" data-testid="sort-menu">
              <button
                v-for="s in SORTS" :key="s.key" role="menuitemradio" :aria-checked="s.key === sort"
                :class="{ on: s.key === sort }" data-testid="sort-item" @click="chooseSort(s.key)"
              >{{ s.label }}</button>
            </div>
          </div>
        </div>
        <TransitionGroup name="row" tag="div" class="list">
          <div
            v-for="sub in rest" :key="sub" class="row" data-testid="mystic-row" :data-sub="sub"
            role="button" tabindex="0" @click="openPage(sub)" @keydown.enter="openPage(sub)"
          >
            <div class="pt">
              <img v-if="art(by[sub])" :src="art(by[sub])" alt="" loading="lazy" />
              <div v-else class="pt-ph"></div>
              <span v-if="by[sub].is_new" class="new">New</span>
            </div>
            <div class="bd">
              <div class="nm">{{ by[sub].name }}</div>
              <div v-if="by[sub].authored_by" class="by">by {{ by[sub].authored_by }}</div>
              <p class="tz">{{ teaserOf(by[sub]) }}</p>
              <div class="meta">
                <template v-for="(seg, j) in facts(by[sub])" :key="seg"><span v-if="j" class="s">·</span>{{ seg }}</template>
                <span v-if="by[sub].social" class="social" data-testid="social">{{ by[sub].social }}</span>
              </div>
            </div>
            <button
              class="star off" data-testid="star" :aria-label="`Add ${by[sub].name} to Your Mystics`"
              @click.stop="star(sub)" @keydown.enter.stop
            >☆</button>
          </div>
        </TransitionGroup>
        <div class="endline">{{ data.readers.length }} Mystics</div>
      </section>
    </div>

    <div class="toast" :class="{ on: toastOn }" aria-live="polite">{{ toastText }}</div>

  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { store } from '@/store'
import LoadState from '@/components/ui/LoadState.vue'
import TodayFromMystics from '@/components/ui/community/TodayFromMystics.vue'
import { useLoadState } from '@/composables/useLoadState'
import { getMysticsTab, mysticMatch, followReader, setFollowOrder } from '@/api-service'
import {
  PLACEHOLDER, NO_MATCH, HOME_STAYS, EMPTY_SLOT, SORTS, sortLabel, startSort, everyone,
  move, withStar, withoutStar, shortName, suggestions, routeOf, factLine, topicLabel, savedSort, saveSort,
} from '@/utils/mystics-tab'

const emit = defineEmits(['open-mystic', 'ask-mystic', 'open-page', 'open-lesson', 'view-deck-gallery',
  'show-lessons', 'request-auth', 'home-changed'])

const data = ref(null)
const mine = ref({ home: null, order: [] })
const sort = ref('shuffle')
const sortOpen = ref(false)
const reorder = ref(false)
const justAdded = ref(null)

const isDesk = ref(false)
const reducedMotion = ref(false)
function readMedia () {
  try {
    isDesk.value = window.matchMedia('(pointer: fine) and (min-width: 761px)').matches
    reducedMotion.value = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  } catch (e) { /* jsdom */ }
}

const by = computed(() => Object.fromEntries(((data.value && data.value.readers) || []).map(r => [r.sub_domain, r])))
const nameOf = (sub) => (by.value[sub] && by.value[sub].name) || ''
const art = (m) => (m && (m.avatar_image || m.background_image)) || ''
const teaserOf = (m) => (m && ((m.today_drop && m.today_drop.teaser) || m.teaser)) || ''
const pricesVisible = computed(() => !!(data.value && data.value.prices_visible) && store.prices_visible !== false)
const facts = (m) => factLine(m, { pricesVisible: pricesVisible.value })
const rest = computed(() => data.value ? everyone(data.value.sort.orders, sort.value, mine.value).filter(s => by.value[s]) : [])

// ── Your Mystics strip (your-mystics-strip 2026-10-30 §1–3) ─────────────
const solo = computed(() => mine.value.order.length === 1 ? mine.value.order[0] : null)
/** The home card's "Today": the drop already in the payload (no new API). */
const soloDrop = computed(() => {
  const m = solo.value && by.value[solo.value]
  return (m && m.today_drop && m.today_drop.title) ? m.today_drop : null
})
/** One Mystic with a drop: the card shows it, so the drops row leaves it out. */
const hideSub = computed(() => soloDrop.value ? solo.value : '')
/** The dashed slot's faces: the first three in Meet the Mystics' current order. */
const faces = computed(() => rest.value.filter(s => art(by.value[s])).slice(0, 3))
const showSlot = computed(() => !reorder.value && mine.value.order.length <= 3)
function toMeet () {
  const h = document.getElementById('mt-all-h')
  if (h && h.scrollIntoView) h.scrollIntoView({ behavior: reducedMotion.value ? 'auto' : 'smooth', block: 'start' })
}

const { loading, failed, run } = useLoadState()
async function load () {
  const body = await run(async () => {
    const b = await getMysticsTab(store.session_id)
    if (!b || !Array.isArray(b.readers)) throw new Error('Unexpected Mystics payload')
    return b
  })
  if (!body) return
  data.value = body
  mine.value = body.mine || { home: null, order: [] }
  sort.value = startSort(body.sort, savedSort())
}

// ── toast ────────────────────────────────────────────────────────────────
const toastText = ref('')
const toastOn = ref(false)
let toastTimer = null
function toast (msg) {
  toastText.value = msg
  toastOn.value = true
  clearTimeout(toastTimer)
  toastTimer = setTimeout(() => { toastOn.value = false }, 2800)
}

// ── ☆ = Follow ───────────────────────────────────────────────────────────
async function star (sub) {
  if (!store.querent_id) { emit('request-auth', { action: 'follow-mystic', subdomain: sub }); return }
  if (mine.value.order.includes(sub)) return
  const before = mine.value
  mine.value = withStar(mine.value, sub)
  justAdded.value = sub
  toast(`${nameOf(sub)} is in Your Mystics.`)
  try {
    const r = await followReader(sub, 'follow', store.session_id)
    if (!r || !r.ok) throw new Error('follow')
  } catch (e) { mine.value = before; toast('That didn’t save. Try again in a moment.') }
}
async function unstar (sub) {
  if (reorder.value) return
  const next = withoutStar(mine.value, sub)
  if (!next) { toast(HOME_STAYS); return }
  const before = mine.value
  mine.value = next
  toast(`${nameOf(sub)} left Your Mystics.`)
  try {
    const r = await followReader(sub, 'unfollow', store.session_id)
    if (!r || !r.ok) throw new Error('unfollow')
  } catch (e) { mine.value = before; toast('That didn’t save. Try again in a moment.') }
}

// ── reorder: ‹ ›, long-press (phone), drag (desktop) ───────────────────
function moveTile (sub, d) { mine.value = { ...mine.value, order: move(mine.value.order, sub, d) } }
async function finishReorder () {
  reorder.value = false
  try {
    const r = await setFollowOrder(mine.value.order, store.session_id)
    if (r && r.mine) mine.value = r.mine
    toast('Order saved.')
  } catch (e) { toast('That didn’t save. Try again in a moment.') }
}
const mineEl = ref(null)
const drag = ref(null)
let pressTimer = null
let suppressClick = false
function onTileDown (e, sub) {
  if (!store.querent_id) return
  if (isDesk.value || reorder.value || e.pointerType === 'mouse') {
    drag.value = { sub, x0: e.clientX, y0: e.clientY, pending: true }
    return
  }
  clearTimeout(pressTimer)
  pressTimer = setTimeout(() => {
    pressTimer = null
    reorder.value = true
    suppressClick = true
    toast('Drag, or use ‹ ›. Tap Done when it looks right.')
  }, 450)
  drag.value = { sub, x0: e.clientX, y0: e.clientY, pending: true, touch: true }
}
function onMove (e) {
  if (!drag.value) return
  const dx = Math.abs(e.clientX - drag.value.x0), dy = Math.abs(e.clientY - drag.value.y0)
  if (pressTimer && dx + dy > 8) { clearTimeout(pressTimer); pressTimer = null; drag.value = null; return }
  if (drag.value.touch && !reorder.value) return
  if (drag.value.pending) {
    if (dx < 6) return
    drag.value = { ...drag.value, pending: false }
    if (!reorder.value) reorder.value = true
  }
  if (e.cancelable) e.preventDefault()
  const box = mineEl.value
  if (!box) return
  const order = mine.value.order
  const tiles = [...box.querySelectorAll('[data-testid="mine-tile"]')]
  const idx = order.indexOf(drag.value.sub)
  for (let i = 0; i < tiles.length; i++) {
    const r = tiles[i].getBoundingClientRect()
    if (i !== idx && e.clientX > r.left && e.clientX < r.right && e.clientY > r.top - 40 && e.clientY < r.bottom + 40) {
      const next = [...order]
      next.splice(idx, 1)
      next.splice(i, 0, drag.value.sub)
      mine.value = { ...mine.value, order: next }
      break
    }
  }
}
function onUp () {
  if (pressTimer) { clearTimeout(pressTimer); pressTimer = null }
  if (drag.value && !drag.value.pending) suppressClick = true
  drag.value = null
}
function onTileClick (sub) {
  if (suppressClick) { suppressClick = false; return }
  if (reorder.value) return
  openPage(sub)
}

// ── the row's edges: soft fades, and ‹ › on desktop when it overflows ────
const edge = ref({ over: false, left: false, right: false })
function readEdges () {
  const b = mineEl.value
  if (!b) { edge.value = { over: false, left: false, right: false }; return }
  const over = b.scrollWidth > b.clientWidth + 2
  edge.value = { over, left: over && b.scrollLeft > 2, right: over && b.scrollLeft + b.clientWidth < b.scrollWidth - 2 }
}
function scrollStrip (d) {
  const b = mineEl.value
  if (b && b.scrollBy) b.scrollBy({ left: d * b.clientWidth * 0.8, behavior: reducedMotion.value ? 'auto' : 'smooth' })
}

// ── motion: portraits glide between the home card and the tiles (FLIP) ──
const rootEl = ref(null)
let flipFrom = null
const orderKey = () => mine.value.order.join('|')
const nOf = (key) => (key ? key.split('|').length : 0)
watch(orderKey, (now, was) => {
  flipFrom = null
  if (reducedMotion.value || !rootEl.value || !was) return
  const rects = {}
  rootEl.value.querySelectorAll('[data-ph]').forEach(el => { rects[el.dataset.ph] = el.getBoundingClientRect() })
  flipFrom = { rects, swap: (nOf(was) < 2) !== (nOf(now) < 2) }
}, { flush: 'pre' })
watch([orderKey, reorder, data], () => {
  readEdges()
  const f = flipFrom
  flipFrom = null
  if (!f || !rootEl.value) return
  const ease = 'cubic-bezier(.2,.8,.2,1)'
  rootEl.value.querySelectorAll('[data-ph]').forEach(el => {
    const a = f.rects[el.dataset.ph]
    if (!a || typeof el.animate !== 'function') return
    const b = el.getBoundingClientRect()
    if (!b.width || !b.height) return
    const dx = a.left - b.left, dy = a.top - b.top, sx = a.width / b.width, sy = a.height / b.height
    if (Math.abs(dx) + Math.abs(dy) < 1 && Math.abs(sx - 1) < 0.01) return
    el.animate([{ transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`, transformOrigin: '0 0' },
      { transform: 'none', transformOrigin: '0 0' }], { duration: 520, easing: ease })
  })
  if (!f.swap) return
  rootEl.value.querySelectorAll('.hc .bd, .snm, .gslot, .homepill').forEach(el => {
    if (typeof el.animate === 'function') el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 380, delay: 140, easing: 'ease-out', fill: 'backwards' })
  })
}, { flush: 'post' })

// ── the box ──────────────────────────────────────────────────────────────
const q = ref('')
const focus = ref(false)
const result = ref(null)
const server = ref({ topics: [], practice: null })
const findEl = ref(null)
let typeTimer = null
const sugg = computed(() => suggestions(q.value, data.value ? data.value.readers : [], data.value ? data.value.vibes : [], server.value))
function onType () {
  clearTimeout(typeTimer)
  const text = q.value.trim()
  if (!text) { server.value = { topics: [], practice: null }; return }
  typeTimer = setTimeout(async () => {
    try {
      const r = await mysticMatch({ q: text, suggest: 1 }, store.session_id)
      if (q.value.trim() === text) server.value = { topics: r.topics || [], practice: r.practice || null }
    } catch (e) { /* the suggestions are a convenience */ }
  }, 220)
}
async function ask (params, typed = '') {
  focus.value = false
  clearTimeout(typeTimer)
  try {
    const r = await mysticMatch(params, store.session_id)
    result.value = { ...r, picks: (r.picks || []).filter(s => by.value[s]), typed }
  } catch (e) {
    result.value = { kind: 'none', title: typed ? `“${typed}”` : '', picks: [], free: false, line: 'Couldn’t reach the Mystics just now.' }
  }
}
function submit () {
  const route = routeOf(q.value, data.value ? data.value.readers : [])
  clearTimeout(typeTimer)
  if (route.kind === 'clear') { clearSearch(); return }
  if (route.kind === 'name') {
    focus.value = false
    result.value = { kind: 'name', title: `Named “${q.value.trim()}”`, picks: route.subs, free: false, line: '' }
    return
  }
  ask({ q: route.q }, route.q)
}
function pickTopic (key, typed = '') {
  if (typed && typed.trim()) { ask({ q: typed.trim() }, typed.trim()); return }
  q.value = topicLabel(key)
  ask({ topic: key })
}
function pickVibe (key) {
  const v = (data.value.vibes || []).find(x => x.key === key)
  q.value = v ? v.label : key
  ask({ vibe: key })
}
function pickName (sub) {
  q.value = nameOf(sub)
  focus.value = false
  result.value = { kind: 'name', title: nameOf(sub), picks: [sub], free: false, line: '' }
}
function clearSearch () {
  q.value = ''
  result.value = null
  focus.value = false
  server.value = { topics: [], practice: null }
}
function lineFor (sub, topic) {
  const m = by.value[sub]
  return (m && topic && m.lines && m.lines[topic]) || ''
}
function getReading (sub) {
  const words = result.value && result.value.free ? (result.value.typed || '') : ''
  emit('ask-mystic', { sub, words })
}

// ── the sort chip ────────────────────────────────────────────────────────
function chooseSort (key) {
  sort.value = key
  sortOpen.value = false
  saveSort(key)
}

// Tapping a Mystic goes straight to their page (Chris, 2026-10-27): the page
// already carries the deck, lessons, ☆ and "Make this my home Mystic".
function openPage (sub) {
  if (by.value[sub]) emit('open-page', sub)
}
// A drop's polaroid opens that Mystic's room (OUTLINE decision 9).
const dropsFull = ref(false)
function openDrop (sub) {
  if (sub) emit('open-page', sub)
}

function onDocClick (e) {
  if (focus.value && findEl.value && !findEl.value.contains(e.target)) focus.value = false
  if (sortOpen.value && !(e.target.closest && e.target.closest('.sortwrap'))) sortOpen.value = false
}
function onKey (e) {
  if (e.key !== 'Escape') return
  if (reorder.value) finishReorder()
  sortOpen.value = false
}

onMounted(() => {
  readMedia()
  load()
  document.addEventListener('click', onDocClick)
  document.addEventListener('keydown', onKey)
  document.addEventListener('pointermove', onMove, { passive: false })
  document.addEventListener('pointerup', onUp)
  document.addEventListener('pointercancel', onUp)
  window.addEventListener('resize', readEdges)
})
onUnmounted(() => {
  document.removeEventListener('click', onDocClick)
  document.removeEventListener('keydown', onKey)
  document.removeEventListener('pointermove', onMove)
  document.removeEventListener('pointerup', onUp)
  document.removeEventListener('pointercancel', onUp)
  window.removeEventListener('resize', readEdges)
  clearTimeout(toastTimer); clearTimeout(typeTimer); clearTimeout(pressTimer)
})
defineExpose({ reload: load })
</script>

<style scoped>
/* Lifted from the prototype (prototype.html), on the app's fonts. */
.mt {
  --gold: #c9a94e; --goldlt: #e7c983; --cream: #fff4dc; --lilac: #c3a9ff; --mist: #b9a6d6; --dim: #8f7fb0;
  --display: 'Cinzel', Georgia, serif; --serif: 'Cormorant Garamond', Georgia, serif;
  --ui: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif; --ease: cubic-bezier(.2,.8,.2,1);
  color: #efe4ff; font-family: var(--ui); padding-bottom: 110px;
}
.mt button { font: inherit; color: inherit; background: none; border: 0; cursor: pointer; padding: 0; }
.mt img { display: block; -webkit-user-drag: none; user-select: none; }
.mt p { margin: 0; }
.wrap { max-width: 1120px; margin: 0 auto; padding: 0 16px; }
.desk .wrap { padding: 0 36px; }
.eyebrow { font: 600 11px var(--display); letter-spacing: .2em; text-transform: uppercase; color: var(--goldlt); }


.find { position: relative; margin-top: 18px; z-index: 3; }
.search { display: flex; align-items: center; gap: 10px; min-height: 50px; padding: 0 6px 0 16px; border-radius: 25px; background: #1a0e30; border: 1px solid rgba(231,201,131,.32); transition: border-color .2s, border-radius .2s; }
.find.open .search { border-color: rgba(255,223,163,.75); border-radius: 25px 25px 0 0; border-bottom-color: rgba(231,201,131,.15); }
.search svg { width: 18px; height: 18px; flex: 0 0 auto; color: var(--goldlt); }
.search input { flex: 1; min-width: 0; background: none; border: 0; outline: 0; color: var(--cream); font: 500 16px var(--ui); }
.search input::placeholder { color: #8f7fb0; }
.search .x { width: 34px; height: 34px; border-radius: 50%; display: grid; place-items: center; background: #2a1845; color: var(--mist); font: 600 16px var(--ui); }
.suggest { position: absolute; left: 0; right: 0; top: 100%; background: #1a0e30; border: 1px solid rgba(255,223,163,.75); border-top: 0; border-radius: 0 0 22px 22px; padding: 6px 14px 14px; box-shadow: 0 24px 40px -16px rgba(0,0,0,.85); animation: drop .22s var(--ease); max-height: 60vh; overflow-y: auto; }
@keyframes drop { from { opacity: 0; transform: translateY(-6px) } to { opacity: 1; transform: none } }
.sg-h { font: 600 10.5px var(--ui); letter-spacing: .09em; text-transform: uppercase; color: var(--dim); margin: 10px 0 7px; }
.sg-chips { display: flex; flex-wrap: wrap; gap: 7px; }
.chip { padding: 7px 12px !important; border-radius: 999px; border: 1px solid rgba(195,169,255,.25) !important; font: 600 13px var(--ui) !important; color: var(--mist) !important; white-space: nowrap; }
.chip:hover, .chip:focus-visible { border-color: rgba(231,201,131,.6) !important; color: #ffdfa3 !important; outline: 0; }
.sg-row { display: flex !important; align-items: center; gap: 10px; width: 100%; padding: 7px 4px !important; border-radius: 10px; text-align: left; }
.sg-row:hover { background: rgba(195,169,255,.07) !important; }
.sg-row img { width: 30px; height: 30px; border-radius: 50%; object-fit: cover; }
.sg-row .k { font: 600 14.5px var(--ui); color: var(--cream); }
.sg-row .s { font: 500 12px var(--ui); color: var(--dim); margin-left: auto; }
.sg-row .gl { width: 30px; height: 30px; border-radius: 50%; display: grid; place-items: center; background: #2a1845; color: var(--goldlt); font: 700 13px var(--serif); }
.sg-foot { margin-top: 10px; font: 500 11.5px/1.4 var(--ui); color: #7a6a9b; }
.sg-foot.first { margin-top: 0; color: #cdbbe9; }

.res { margin-top: 16px; }
.res-h { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; }
.res-h h3 { margin: 0; font: 700 20px var(--serif); color: var(--cream); }
.res-h button { font: 600 12.5px var(--ui) !important; color: var(--lilac) !important; }
.pick-list { display: grid; gap: 10px; margin-top: 10px; }
.desk .pick-list { grid-template-columns: repeat(3, 1fr); }
.pick { display: flex; flex-direction: column; gap: 9px; padding: 13px; border-radius: 16px; background: rgba(255,255,255,.035); border: 1px solid rgba(195,169,255,.14); opacity: 0; transform: translateY(10px); animation: rise .5s var(--ease) forwards; cursor: pointer; }
.pick:hover { border-color: rgba(231,201,131,.4); }
@keyframes rise { to { opacity: 1; transform: none } }
.pick .hd { display: flex; align-items: center; gap: 10px; }
.pick .hd img { width: 46px; height: 46px; border-radius: 50%; object-fit: cover; border: 1px solid rgba(195,169,255,.3); }
.pick .nm { font: 700 18px/1.1 var(--serif); color: var(--cream); }
.by { font: 500 11.5px var(--ui); color: var(--dim); }
.tag { display: inline-block; margin-left: 6px; padding: 2px 7px; border-radius: 999px; background: rgba(231,201,131,.16); color: var(--goldlt); font: 700 10px var(--ui); letter-spacing: .06em; text-transform: uppercase; vertical-align: 2px; }
.quote { font: italic 500 16px/1.35 var(--serif); color: #efe2ff; padding-left: 10px; border-left: 2px solid rgba(231,201,131,.5); }
.quote.soft { border-left-color: rgba(195,169,255,.3); }
.qlabel { font: 600 10.5px var(--ui); letter-spacing: .08em; text-transform: uppercase; color: var(--dim); margin-bottom: 4px; }
.pick .ft { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: auto; }
.meta { font: 500 12px var(--ui); color: var(--mist); }
.meta .s { opacity: .45; margin: 0 5px; }
.gold { display: inline-flex !important; align-items: center; justify-content: center; gap: 6px; padding: 10px 16px !important; border-radius: 999px; background: linear-gradient(180deg,#f3dc9f,#d7b45e) !important; color: #2a1845 !important; font: 700 14px var(--serif) !important; box-shadow: 0 6px 16px -8px rgba(231,201,131,.6); white-space: nowrap; }
.ghost { display: inline-flex !important; align-items: center; justify-content: center; gap: 6px; padding: 9px 14px !important; border-radius: 999px; border: 1px solid rgba(195,169,255,.35) !important; color: var(--lilac) !important; font: 600 13px var(--ui) !important; white-space: nowrap; }
.pick .gold { padding: 8px 13px !important; font-size: 13.5px !important; }
.after { margin-top: 10px; font: 500 12.5px/1.45 var(--ui); color: var(--dim); }
.after button { color: var(--lilac) !important; text-decoration: underline; text-underline-offset: 3px; }
.empty { margin-top: 10px; padding: 14px; border-radius: 14px; border: 1px dashed rgba(195,169,255,.25); font: 500 14px/1.45 var(--ui); color: #cdbbe9; }

.sec { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin: 22px 0 10px; }
.sec h3 { margin: 0; font: 700 20px var(--serif); color: var(--cream); display: flex; align-items: center; gap: 8px; }
.sec .hint { font: 500 11.5px var(--ui) !important; color: var(--dim) !important; }
.sec .done { padding: 6px 14px !important; font-size: 13px !important; }
.pt-ph { width: 100%; height: 100%; background: linear-gradient(135deg,#4e2776,#1a0e30); }
.star { position: absolute; right: 6px; top: 6px; width: 32px; height: 32px; border-radius: 50%; display: grid !important; place-items: center; background: rgba(13,5,32,.62) !important; color: #e7c983 !important; font-size: 15px !important; line-height: 1; }
.star.off { color: #b9a6d6 !important; }
@keyframes wig { from { transform: rotate(-1.4deg) } to { transform: rotate(1.4deg) } }
@keyframes inn { from { opacity: 0; transform: scale(.7) } to { opacity: 1; transform: none } }

/* ── Your Mystics strip (your-mystics-strip 2026-10-30, prototype "NEW") ── */
#mt-all-h { scroll-margin-top: 70px; }
.sec .right { display: flex; align-items: center; gap: 10px; }
.arrows { display: flex; gap: 6px; }
.arrows button { width: 30px; height: 30px; border-radius: 50%; display: grid !important; place-items: center; background: #1a0e30 !important; border: 1px solid rgba(195,169,255,.22) !important; color: var(--goldlt) !important; font: 700 15px var(--ui) !important; }
.arrows button:disabled { opacity: .3; cursor: default; }

/* One Mystic: the home card, and beside it the way to more. */
.solo { display: grid; grid-template-columns: 1fr; gap: 10px; }
@media (min-width: 600px) { .solo { grid-template-columns: minmax(0, 1.55fr) minmax(0, 1fr); } }
.hc { position: relative; display: flex; align-items: center; gap: 14px; padding: 12px 52px 12px 12px; border-radius: 16px; cursor: pointer; text-align: left;
  background: linear-gradient(135deg, rgba(231,201,131,.09), rgba(123,75,176,.14)); border: 1px solid rgba(231,201,131,.42); transition: border-color .2s, transform .2s var(--ease); }
.hc:hover, .hc:focus-visible { border-color: rgba(231,201,131,.8); outline: 0; }
.hc:active { transform: scale(.99); }
.hc .ph { position: relative; flex: 0 0 auto; width: 64px; height: 64px; border-radius: 13px; overflow: hidden; border: 1.5px solid rgba(231,201,131,.85); box-shadow: 0 0 0 3px rgba(231,201,131,.12); background: #1a0e30; }
.hc .ph img { width: 100%; height: 100%; object-fit: cover; pointer-events: none; }
.hc .bd { min-width: 0; flex: 1; }
.hc .nmrow { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.hc .nm { font: 700 19px/1.1 var(--serif); color: var(--cream); }
.homepill { display: inline-flex; align-items: center; gap: 4px; padding: 2px 7px 2px 5px; border-radius: 999px; background: #e7c983; color: #2a1845; font: 800 9.5px var(--ui); letter-spacing: .06em; text-transform: uppercase; line-height: 1.3; }
.homepill svg { width: 11px; height: 11px; }
.hc .today { margin-top: 5px; display: flex; align-items: baseline; gap: 6px; min-width: 0; }
.hc .today .ey { flex: 0 0 auto; font: 600 10px var(--ui); letter-spacing: .1em; text-transform: uppercase; color: var(--goldlt); }
.hc .today .tt { font: 700 15.5px/1.2 var(--serif); color: var(--cream); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.hc .ln { margin-top: 1px; font: italic 500 14.5px/1.3 var(--serif); color: #cdbbe9; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.hc .hstar { position: absolute; top: 8px; right: 8px; width: 36px; height: 36px; border-radius: 50%; display: grid !important; place-items: center; background: rgba(13,5,32,.5) !important; color: #e7c983 !important; font-size: 16px !important; }

/* The dashed slot (EMPTY_SLOT): three faces from Meet the Mystics; a tap scrolls there. */
.gslot { display: flex !important; align-items: center; gap: 12px; padding: 10px 14px !important; border-radius: 16px; border: 1px dashed rgba(195,169,255,.32) !important; color: #cdbbe9 !important; text-align: left; transition: border-color .2s, background .2s; }
.gslot:hover, .gslot:focus-visible { border-color: rgba(231,201,131,.6) !important; background: rgba(195,169,255,.05) !important; outline: 0; }
.gslot .faces { display: flex; flex: 0 0 auto; }
.gslot .faces img { width: 30px; height: 30px; border-radius: 8px; object-fit: cover; border: 2px solid #0a0418; margin-left: -9px; opacity: .92; }
.gslot .faces img:first-child { margin-left: 0; }
.gslot .gt { font: 500 13px/1.35 var(--ui); flex: 1; min-width: 0; }
.gslot .ar { flex: 0 0 auto; color: var(--goldlt); font: 600 15px var(--ui); }
.solo .gslot { min-height: 52px; }
@media (min-width: 600px) { .solo .gslot { flex-direction: column; align-items: flex-start; justify-content: center; gap: 8px; padding: 12px 16px !important; }
  .solo .gslot .ar { display: none; } }

/* Two or more: small tiles in their order, one row, sideways. */
.strip { position: relative; }
.mine2 { display: flex; gap: 14px; overflow-x: auto; margin: 0 -16px; padding: 8px 16px 6px; scrollbar-width: none; scroll-snap-type: x proximity; scroll-padding-inline: 16px; touch-action: pan-x; align-items: flex-start; }
.mine2::-webkit-scrollbar { display: none; }
.mine2.reorder { touch-action: none; }
.desk .mine2 { margin: 0 -4px; padding: 8px 4px 6px; scroll-padding-inline: 4px; }
.desk .mine2.reorder { flex-wrap: wrap; overflow: visible; }
.mine2.fade-r { -webkit-mask-image: linear-gradient(90deg, #000 calc(100% - 48px), transparent); mask-image: linear-gradient(90deg, #000 calc(100% - 48px), transparent); }
.mine2.fade-l.fade-r { -webkit-mask-image: linear-gradient(90deg, transparent, #000 36px, #000 calc(100% - 48px), transparent); mask-image: linear-gradient(90deg, transparent, #000 36px, #000 calc(100% - 48px), transparent); }
.mine2.fade-l:not(.fade-r) { -webkit-mask-image: linear-gradient(90deg, transparent, #000 36px); mask-image: linear-gradient(90deg, transparent, #000 36px); }
.st { position: relative; flex: 0 0 76px; width: 76px; scroll-snap-align: start; user-select: none; -webkit-touch-callout: none; transition: transform .25s var(--ease); }
.desk .st { flex-basis: 84px; width: 84px; }
.st .sp { position: relative; width: 68px; height: 68px; margin: 0 auto; border-radius: 14px; cursor: pointer; }
.desk .st .sp { width: 76px; height: 76px; }
.st .sp:focus-visible { outline: 2px solid rgba(231,201,131,.7); outline-offset: 2px; }
.st .sp .im { position: absolute; inset: 0; border-radius: inherit; overflow: hidden; border: 1px solid rgba(195,169,255,.2); box-shadow: 0 8px 16px -12px #000; background: #1a0e30; transition: border-color .2s; }
.st .sp:hover .im { border-color: rgba(231,201,131,.6); }
.st .sp img { width: 100%; height: 100%; object-fit: cover; pointer-events: none; }
.st.home .sp .im { border: 1.5px solid rgba(231,201,131,.85); box-shadow: 0 0 0 3px rgba(231,201,131,.12), 0 8px 16px -12px #000; }
.st .homepill { position: absolute; left: 50%; bottom: -7px; transform: translateX(-50%); padding: 1px 6px 1px 4px; font-size: 8.5px; box-shadow: 0 2px 6px rgba(0,0,0,.5); z-index: 2; }
.st .homepill svg { width: 10px; height: 10px; }
.st .sstar { position: absolute; top: -6px; right: -6px; width: 24px; height: 24px; border-radius: 50%; display: grid !important; place-items: center; background: #1a0e30 !important; border: 1px solid rgba(231,201,131,.5) !important; color: #e7c983 !important; font-size: 11.5px !important; line-height: 1; z-index: 2; }
.st .sstar::before { content: ''; position: absolute; inset: -9px; }
.st .snm { margin-top: 12px; text-align: center; font: 700 14px/1.1 var(--serif); color: var(--cream); display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.st .mv { display: none; gap: 5px; margin-top: 6px; }
.reorder .st .mv { display: flex; }
.mv button { flex: 1; height: 28px; border-radius: 8px; background: #2a1845 !important; color: var(--goldlt) !important; font: 700 15px var(--ui) !important; }
.mv button:disabled { opacity: .3; }
.reorder .st { animation: wig .32s ease-in-out infinite alternate; }
.reorder .st:nth-child(2n) { animation-delay: .16s; }
.reorder .st .sstar { visibility: hidden; }
.st.dragging { animation: none !important; transform: scale(1.08) rotate(-2deg); z-index: 5; opacity: .92; }
.st.new-in { animation: inn .5s var(--ease); }
.mine2 .gslot { flex: 0 0 168px; flex-direction: column; align-items: flex-start; justify-content: center; gap: 8px; height: 90px; margin-left: 4px; padding: 10px 12px !important; scroll-snap-align: start; }
.desk .mine2 .gslot { flex-basis: 200px; height: 98px; }
.mine2 .gslot .gt { font-size: 12.5px; }
.mine2 .gslot .faces img { width: 26px; height: 26px; }

.sortwrap { position: relative; flex: none; }
.sortchip { white-space: nowrap; }
@media (max-width: 400px) { [data-testid="everyone-here"] .sec h3 { font-size: 18px; white-space: nowrap; } }
.sortchip { padding: 6px 12px !important; border-radius: 999px; border: 1px solid rgba(240,217,163,.5) !important; background: linear-gradient(180deg,#7b4bb0,#4e2776) !important; color: #ffdfa3 !important; font: 600 12.5px var(--ui) !important; min-height: 32px; }
.sortmenu { position: absolute; right: 0; top: calc(100% + 6px); z-index: 4; min-width: 160px; padding: 6px; border-radius: 14px; background: #1a0e30; border: 1px solid rgba(231,201,131,.4); box-shadow: 0 18px 30px -12px rgba(0,0,0,.85); animation: drop .18s var(--ease); display: flex; flex-direction: column; }
.sortmenu button { text-align: left; padding: 9px 12px !important; border-radius: 10px; font: 600 13.5px var(--ui) !important; color: var(--mist) !important; }
.sortmenu button.on { color: #ffdfa3 !important; background: rgba(123,75,176,.35) !important; }
.sortmenu button:hover { background: rgba(195,169,255,.08) !important; }
.list { display: grid; gap: 10px; }
.desk .list { grid-template-columns: repeat(3, 1fr); gap: 12px; }
.row { position: relative; display: flex; gap: 12px; padding: 10px; border-radius: 14px; background: rgba(255,255,255,.03); border: 1px solid rgba(255,255,255,.07); cursor: pointer; transition: border-color .2s, transform .3s var(--ease), opacity .3s; }
.row:hover, .row:focus-visible { border-color: rgba(231,201,131,.38); outline: 0; }
.row .pt { width: 64px; height: 80px; border-radius: 10px; overflow: hidden; flex: 0 0 auto; position: relative; }
.row .pt img { width: 100%; height: 100%; object-fit: cover; }
.row .bd { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; padding-right: 30px; }
.row .nm { font: 700 17.5px/1.1 var(--serif); color: var(--cream); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.row .tz { font: italic 500 14.5px/1.28 var(--serif); color: #cdbbe9; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; margin-top: 2px; }
.row .meta { margin-top: auto; padding-top: 3px; }
.row .social { display: block; margin-top: 2px; color: var(--goldlt); font: 600 11.5px var(--ui); }
.row .star { top: 6px; right: 6px; background: transparent !important; font-size: 19px !important; width: 36px; height: 36px; }
.row-leave-active { transition: opacity .3s, transform .3s var(--ease); }
.row-leave-to { opacity: 0; transform: translateY(-14px) scale(.97); }
.row-move { transition: transform .3s var(--ease); }
.new { position: absolute; left: 4px; top: 4px; padding: 1px 6px; border-radius: 6px; background: #e7c983; color: #2a1845; font: 800 9.5px var(--ui); letter-spacing: .06em; text-transform: uppercase; }
.endline { text-align: center; margin: 18px 0 6px; font: 500 12px var(--ui); color: #6e5d92; }

/* From the top, under the portrait, clear of the results and the bar (loop walk 2026-10-26 #8). */
.toast { position: fixed; left: 50%; top: calc(env(safe-area-inset-top, 0px) + 66px); transform: translate(-50%, -20px); opacity: 0; z-index: 1210; padding: 10px 16px; border-radius: 12px; background: #f4ecdc; color: #2a1845; font: 600 13px/1.35 var(--ui); max-width: min(330px, calc(100vw - 32px)); text-align: center; transition: opacity .25s, transform .25s var(--ease); pointer-events: none; }
.toast.on { opacity: 1; transform: translate(-50%, 0); }

.enter > * { animation: rise .55s var(--ease) both; }
.enter > *:nth-child(2) { animation-delay: .05s; } .enter > *:nth-child(3) { animation-delay: .1s; } .enter > *:nth-child(4) { animation-delay: .15s; } .enter > *:nth-child(5) { animation-delay: .2s; }
.rm *, .rm *::before, .rm *::after { animation: none !important; transition: none !important; }
.rm .pick, .rm .enter > * { opacity: 1 !important; transform: none !important; }
@media (prefers-reduced-motion: reduce) {
  .mt *, .mt *::before, .mt *::after { animation: none !important; transition: none !important; }
  .pick, .enter > * { opacity: 1 !important; transform: none !important; }
}
</style>
